// 本地 API 客户端 - 直接调本地 Express 后端
import type {
  DashboardStats,
  PlanningEntity,
  LiveDataEntity,
  LiveReviewEntity,
  AIAnalysisRequest,
  AIAnalysisResponse
} from '@shared/types';

const BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    ...options
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

export const api = {
  // Dashboard
  getDashboardStats: () => request<DashboardStats>('/dashboard/stats'),

  // Plannings
  getPlannings: () => request<PlanningEntity[]>('/plannings'),
  getPlanning: (id: string) => request<PlanningEntity>(`/plannings/${id}`),
  createPlanning: (data: Partial<PlanningEntity>) =>
    request<PlanningEntity>('/plannings', { method: 'POST', body: JSON.stringify(data) }),
  updatePlanning: (id: string, data: Partial<PlanningEntity>) =>
    request<PlanningEntity>(`/plannings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePlanning: (id: string) =>
    request<{ success: boolean }>(`/plannings/${id}`, { method: 'DELETE' }),
  generatePlanningAdvice: (data: any) =>
    request<{ scriptOutline: string; tasks: { name: string; completed: boolean }[]; suggestions: { title: string; content: string }[] }>(
      '/plannings/ai-generate',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  // Live Data
  getLiveData: () => request<LiveDataEntity[]>('/live-data'),
  getLiveDataById: (id: string) => request<LiveDataEntity>(`/live-data/${id}`),
  getLiveDataByPlanning: (planningId: string) =>
    request<LiveDataEntity | null>(`/live-data/by-planning/${planningId}`),
  createLiveData: (data: Partial<LiveDataEntity>) =>
    request<LiveDataEntity>('/live-data', { method: 'POST', body: JSON.stringify(data) }),
  updateLiveData: (id: string, data: Partial<LiveDataEntity>) =>
    request<LiveDataEntity>(`/live-data/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLiveData: (id: string) =>
    request<{ success: boolean }>(`/live-data/${id}`, { method: 'DELETE' }),

  // Live Reviews
  getLiveReviews: () => request<LiveReviewEntity[]>('/live-reviews'),
  getLiveReview: (id: string) => request<LiveReviewEntity>(`/live-reviews/${id}`),
  getLiveReviewByLiveData: (liveDataId: string) =>
    request<LiveReviewEntity | null>(`/live-reviews/by-live-data/${liveDataId}`),
  updateLiveReview: (id: string, data: Partial<LiveReviewEntity>) =>
    request<LiveReviewEntity>(`/live-reviews/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  createReviewFromLiveData: (liveDataId: string) =>
    request<LiveReviewEntity>(`/live-reviews/from-live-data/${liveDataId}`, { method: 'POST' }),

  // AI
  analyzeLiveReview: (data: AIAnalysisRequest) =>
    request<AIAnalysisResponse>('/ai-analysis/live-review', { method: 'POST', body: JSON.stringify(data) }),

  // Meta
  reset: () => request<{ ok: boolean }>('/meta/reset', { method: 'POST' })
};
